import { Box, SxProps } from '@mui/material';
import { GridActionsCellItem, GridCellParams, GridColDef } from '@mui/x-data-grid-pro';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { getCohortBuilderPath } from '@verantos-dev/core-constants';
import { CohortBuilderPageTab } from '@verantos-dev/core-enums';
import { cohortApiSlice } from '@verantos-dev/data-access-cohorts';
import { type DataGridMessages, Icon, StatusDisplay } from '@verantos-dev/shared-components';
import { theme } from '@verantos-dev/shared-styles';
import { formatDate } from '@verantos-dev/shared-utils';
import { type Cohort } from '@verantos-dev/types-cohorts';

import { CohortsListActionMenu } from '../containers/CohortsListActionMenu/CohortsListActionMenu';

const stringCellStyles: SxProps = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  lineHeight: '1.2',
  whiteSpace: 'normal',
};

const clickableCellStyle: SxProps = {
  ...stringCellStyles,
  color: theme.palette.primary.main,
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  },
};

export const useCohortsListPageContent = () => {
  const intl = useIntl();
  const navigate = useNavigate();

  const messages: DataGridMessages = {
    noRows: intl.formatMessage({ id: 'projects.cohortsList.datagrid.noRows' }),
    noResults: intl.formatMessage({ id: 'projects.cohortsList.datagrid.noResults' }),
    error: intl.formatMessage({ id: 'projects.cohortsList.datagrid.errorMessage' }),
  };

  const onCohortCellClick = (params: GridCellParams) => {
    const projectId = params.row.projectId;
    const cohortId = params.id.toString();
    const cohortVersionId = params.row.latestCohortVersionId;
    const field = params.field;

    if (field === 'cohortName') {
      cohortApiSlice.endpoints.getCohort.initiate({ projectId, cohortId });
      cohortApiSlice.endpoints.getCohortVersion.initiate({ projectId, cohortId, cohortVersionId });
      navigate(
        `${getCohortBuilderPath(projectId, cohortId, cohortVersionId)}?pageTab=${
          CohortBuilderPageTab.events
        }`
      );
    }
  };

  const columnDefinition: GridColDef<Cohort>[] = [
    {
      headerName: intl.formatMessage({
        id: 'cohortsList.columnHeader.name',
        defaultMessage: 'Name',
      }),
      type: 'string',
      field: 'cohortName',
      flex: 1,
      minWidth: 330,
      renderCell: (params) => {
        const cohortName = params?.row.cohortName;
        const cohortId = params?.row.cohortProfileId;
        return (
          <Box data-testid={`cohort-name-${cohortId}`} sx={clickableCellStyle}>
            {cohortName}
          </Box>
        );
      },
    },
    {
      headerName: intl.formatMessage({ id: 'cohortsList.columnHeader.status' }),
      type: 'string',
      field: 'latestStatus',
      minWidth: 130,
      flex: 0,
      renderCell: (params) => {
        const status = params?.row.latestStatus;
        const cohortId = params?.row.cohortProfileId;
        return (
          <StatusDisplay
            id={`cohort-status-${cohortId}`}
            status={`cohortstatus${status.toLowerCase()}`}
          />
        );
      },
    },
    {
      headerName: intl.formatMessage({ id: 'cohortsList.columnHeader.subjects' }),
      type: 'number',
      field: 'latestSubjectCount',
      minWidth: 150,
      flex: 0.5,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        const latestSubjectCount =
          params?.row.latestSubjectCount >= 0
            ? intl.formatNumber(params.row.latestSubjectCount)
            : '--';
        const cohortId = params?.row.cohortProfileId;
        return (
          <Box data-testid={`cohort-latestSubjectCount-${cohortId}`} sx={stringCellStyles}>
            {latestSubjectCount}
          </Box>
        );
      },
    },
    {
      headerName: intl.formatMessage({ id: 'cohortsList.columnHeader.owner' }),
      type: 'string',
      field: 'createdBy.firstName',
      minWidth: 330,
      flex: 0.5,
      valueGetter: (_, row) => {
        const owner = `${row.createdBy.firstName} ${row.createdBy.lastName}`;
        return owner;
      },
      renderCell: (params) => {
        const owner = `${params?.row.createdBy.firstName} ${params?.row.createdBy.lastName}`;
        const cohortId = params?.row.cohortProfileId;
        return (
          <Box data-testid={`cohort-owner-${cohortId}`} sx={stringCellStyles}>
            {owner}
          </Box>
        );
      },
    },
    {
      headerName: intl.formatMessage({ id: 'cohortsList.columnHeader.modifiedOn' }),
      type: 'dateTime',
      field: 'modifiedBy.transactionDate',
      minWidth: 240,
      flex: 0.5,
      valueGetter: (_, row) => {
        return new Date(row.modifiedBy.transactionDate);
      },
      renderCell: (params) => {
        const modifiedOn = params.row.modifiedBy.transactionDate
          ? formatDate(params.row.modifiedBy.transactionDate, 'dateTimeFull')
          : '--';
        const cohortId = params?.row.cohortProfileId;
        return (
          <Box data-testid={`cohort-owner-${cohortId}`} sx={stringCellStyles}>
            {modifiedOn}
          </Box>
        );
      },
    },
    {
      field: '',
      headerName: intl.formatMessage({ id: 'common.grid.columnName.actions' }),
      headerAlign: 'right',
      align: 'right',
      flex: 0,
      width: 80,
      type: 'actions',
      renderCell: (param) => {
        return <CohortsListActionMenu cohort={param.row} />;
      },
      renderHeader: () => null,
    },
  ];

  return {
    messages,
    onCohortCellClick,
    columnDefinition,
  };
};
